import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import AttachFile from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import AddCommentOutlined from '@mui/icons-material/AddCommentOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';

import axiosInstance, { applyAuthHeaders } from '../../features/axiosInstance';
import aiService from '../../features/ai/aiService';
import { useAuth } from '../../contexts/authContext';

const drawerWidth = 360;

// Keep in step with express.json({ limit: '50MB' }) on the server: base64
// adds ~33%, so anything much larger than this is rejected before it is sent.
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

// How long to wait after the last change before writing the thread, so a
// streamed reply settles before it is stored instead of writing on every
// progress event.
const SAVE_DEBOUNCE_MS = 800;

// The types the server can turn into text; keep them in step with the
// parsers in server/services/ai.file.js.
const SUPPORTED_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.docx', '.csv', '.txt'];
const SUPPORTED_ACCEPT = [
  ...SUPPORTED_EXTENSIONS,
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'text/plain'
].join(',');

// Matched on the extension rather than the MIME type, the same way the server
// picks a parser: browsers report .csv and .xls inconsistently.
const isSupportedFile = (file) =>
  SUPPORTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

// Reads an attached file as a data URL. Every type goes to the server to be
// converted to text, so this is the only reader the sidebar needs.
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`${file.name} could not be read.`));
    reader.readAsDataURL(file);
  });

// The server extracts and stores the text; the sidebar only keeps the id, name
// and type it gets back, so the file contents never sit in the chat state. The
// model reads them on demand through its readAttachment tool. Resolves to
// `{ id, name, type }`.
const uploadAttachment = async (file) => {
  const dataUrl = await readFileAsDataUrl(file);
  // The endpoint takes the payload only, so drop the `data:...;base64,` head.
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const response = await axiosInstance.post('/ai/attachment', { base64, fileName: file.name });

  return response.data;
};

// An aborted request rejects as a cancel; the signal is checked too because it
// is already set by the time the user hits stop. `fetch` reports an abort as
// an AbortError where axios reports a canceled error code.
const isAborted = (error, signal) =>
  Boolean(signal?.aborted) ||
  error?.name === 'AbortError' ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError';

// Reads the chat endpoint's Server-Sent Events body and hands each parsed
// payload to `onEvent`. The server writes one `data:` line per event and
// separates events with a blank line; reply text arrives JSON-escaped, so a
// payload can never contain the separator itself. Resolves once the server
// closes the stream, and rejects through the reader when the request is
// aborted.
const readChatStream = async (response, onEvent) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      const dataLine = block.split('\n').find((line) => line.startsWith('data:'));
      if (dataLine) {
        onEvent(JSON.parse(dataLine.slice('data:'.length).trim()));
      }

      boundary = buffer.indexOf('\n\n');
    }
  }
};

// A short "how long ago" label for the history menu: minutes and hours under
// a day, "yesterday", days under a week, then the date.
const formatRelativeTime = (updatedAt) => {
  const then = new Date(updatedAt);
  if (Number.isNaN(then.getTime())) {
    return '';
  }

  const minutes = Math.floor((Date.now() - then.getTime()) / 60000);
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) {
    return 'yesterday';
  }
  if (days < 7) {
    return `${days}d ago`;
  }

  return then.toLocaleDateString();
};

export default function AiSidebar() {
  const { currentWorkspace } = useAuth();
  const workspaceId = currentWorkspace?.id || null;

  const [open, setOpen] = useState(false);
  // Whether this workspace has an assistant at all. Off until the server says
  // otherwise, so the icon cannot appear for a workspace that has none.
  const [aiEnabled, setAiEnabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // True while the stored thread is being fetched on open, so the sidebar
  // shows its spinner instead of a stale or empty thread.
  const [restoring, setRestoring] = useState(false);
  // What the assistant is doing right now, reported by the stream while it
  // works; empty between requests.
  const [status, setStatus] = useState('');
  // Seconds the current request has been running; shown beside the status so
  // a slow tool round reads as working instead of hung.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [attaching, setAttaching] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  // The freshest thread and its id, mirrored out of state so the debounced
  // save and the flush never read a stale render.
  const messagesRef = useRef(messages);
  const conversationIdRef = useRef(null);
  // Bumped whenever the open thread is replaced (new chat, opening another
  // conversation), so a save still in flight for the old thread cannot adopt
  // the new one's id.
  const epochRef = useRef(0);
  const saveTimerRef = useRef(null);
  // Saves are chained so a slow first save (the one that creates the
  // conversation) can never race the next write into creating a duplicate.
  const saveChainRef = useRef(Promise.resolve());

  // The workspace decides whether there is an assistant to open, so the icon
  // is only drawn once the server has said so - and a workspace with no config,
  // or a check that fails, leaves it off rather than offering a chat the server
  // would refuse.
  useEffect(() => {
    // The workspace that was showing a moment ago has no say here: until this
    // one's own status lands the assistant stays away.
    setAiEnabled(false);

    if (!workspaceId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { enabled } = await aiService.getStatus();
        if (!cancelled) {
          setAiEnabled(Boolean(enabled));
        }
      } catch (error) {
        if (!cancelled) {
          setAiEnabled(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, status, open]);

  // Ticks once per second while a request runs so the status can show its
  // age; each request restarts the count.
  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [loading]);

  // Restore the stored thread whenever the drawer opens or the active
  // workspace changes: the id remembered from the last visit first, then the
  // newest conversation, then a blank chat. Anything that survives a reload
  // comes from the server - nothing is kept in component state.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    const restore = async () => {
      setRestoring(true);

      try {
        let conversation = null;
        const savedId = window.localStorage.getItem('aiConversationId');

        // The saved id may be stale (conversation deleted) or belong to the
        // previous workspace, in which case the server answers 404 and the
        // newest conversation of this workspace takes over.
        if (savedId) {
          conversation = await aiService.getConversation(savedId).catch(() => null);
        }

        if (!conversation) {
          const conversations = await aiService.listConversations().catch(() => []);
          if (conversations.length > 0) {
            conversation = await aiService.getConversation(conversations[0].id).catch(() => null);
          }
        }

        if (cancelled) {
          return;
        }

        if (conversation) {
          epochRef.current += 1;
          conversationIdRef.current = conversation.id;
          window.localStorage.setItem('aiConversationId', conversation.id);
          setMessages(Array.isArray(conversation.messages) ? conversation.messages : []);
        } else {
          epochRef.current += 1;
          conversationIdRef.current = null;
          window.localStorage.removeItem('aiConversationId');
          setMessages([]);
        }
      } finally {
        if (!cancelled) {
          setRestoring(false);
        }
      }
    };

    restore();

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const busy = loading || attaching || restoring;

  // Writes the given thread to its conversation, creating the conversation on
  // the first save when there is no id yet. The id is adopted only while the
  // user is still on the thread this save belongs to, so a save racing a "new
  // chat" or another loaded conversation cannot hijack the current thread's id.
  const persistConversation = async (messagesToSave) => {
    const epoch = epochRef.current;
    let id = conversationIdRef.current;

    try {
      if (!id) {
        const created = await aiService.createConversation();
        id = created.id;
      }

      await aiService.saveConversation(id, { messages: messagesToSave });

      if (epochRef.current === epoch) {
        conversationIdRef.current = id;
        window.localStorage.setItem('aiConversationId', id);
      }
    } catch (error) {
      // Persistence is best-effort: a failed save must never break the chat
      // itself, the thread simply will not survive a reload.
    }
  };

  // Arms the debounced write with the freshest thread; a streamed reply
  // appends once but progress events arrive every second, so the timer is
  // re-armed until the thread settles.
  const scheduleSave = (nextMessages) => {
    messagesRef.current = nextMessages;
    clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      const current = messagesRef.current;
      // An empty thread (a new chat with nothing said yet) is not persisted.
      if (current.length === 0) {
        return;
      }

      saveChainRef.current = saveChainRef.current.then(() => persistConversation(current));
    }, SAVE_DEBOUNCE_MS);
  };

  // Writes the thread's unsaved tail now and waits for it; callers flush
  // right before replacing or leaving the thread so its latest state lands in
  // the history.
  const flushPendingSave = async () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;

    const current = messagesRef.current;
    if (current.length === 0) {
      return;
    }

    saveChainRef.current = saveChainRef.current.then(() => persistConversation(current));
    await saveChainRef.current;
  };

  const startNewChat = async () => {
    // The old thread keeps its latest state in the history; the new one
    // starts empty and its id is only created on its first save.
    await flushPendingSave();

    epochRef.current += 1;
    conversationIdRef.current = null;
    window.localStorage.removeItem('aiConversationId');
    setMessages([]);
    setInput('');
    setPendingAttachments([]);
    setStatus('');
  };

  const handleClose = () => {
    // Write any unsaved tail before the drawer goes away, so reopening finds
    // the thread exactly as it was left.
    flushPendingSave();
    setHistoryAnchorEl(null);
    setRestoring(false);
    setOpen(false);
  };

  const handleOpenHistory = async (event) => {
    setHistoryAnchorEl(event.currentTarget);

    // Refresh every time the menu opens so a chat saved elsewhere shows up
    // without a reload.
    const conversations = await aiService.listConversations().catch(() => []);
    setHistoryItems(conversations.slice(0, 15));
  };

  const handleOpenConversation = async (id) => {
    setHistoryAnchorEl(null);

    // Switching threads flushes the current one first so it keeps its latest
    // state in the history.
    await flushPendingSave();

    const conversation = await aiService.getConversation(id).catch(() => null);

    // Gone since the menu listed it (deleted elsewhere): fall back to a
    // fresh chat.
    if (!conversation) {
      await startNewChat();
      return;
    }

    epochRef.current += 1;
    conversationIdRef.current = conversation.id;
    window.localStorage.setItem('aiConversationId', conversation.id);
    setMessages(Array.isArray(conversation.messages) ? conversation.messages : []);
  };

  const handleDeleteConversation = async (id) => {
    const wasOpen = conversationIdRef.current === id;

    // Deleting the open thread starts a new chat so the sidebar never keeps
    // writing to a conversation that no longer exists.
    if (wasOpen) {
      await startNewChat();
    }

    await aiService.deleteConversation(id).catch(() => {});

    // Keep the open menu in step without reopening it.
    setHistoryItems((current) => current.filter((item) => item.id !== id));
  };

  const addAssistantMessage = (content) =>
    setMessages((current) => {
      const next = [...current, { role: 'assistant', content }];
      // Every assistant reply is persisted too, including a "(stopped)" or
      // error note, so the stored thread matches what the user sees.
      scheduleSave(next);
      return next;
    });

  const sendMessages = async (nextMessages) => {
    setMessages(nextMessages);
    // The user's message is persisted (debounced) straight away, before the
    // reply arrives.
    scheduleSave(nextMessages);
    setLoading(true);
    setStatus('');
    setElapsedSeconds(0);

    const controller = new AbortController();
    abortRef.current = controller;

    // Set once the server says it is done, so a stop arriving after the reply
    // has landed is not reported as a stop.
    let finished = false;
    // Set when the server reports an error event, so a stream that then ends
    // without `done` is not reported a second time.
    let receivedError = false;

    try {
      // `fetch` rather than the axios instance: the reply arrives as a stream
      // the UI reads chunk by chunk, and axios only resolves on a full body.
      const response = await fetch('/api/ai/chat/stream', {
        method: 'POST',
        headers: await applyAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ messages: nextMessages }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error((await response.text()) || `Request failed (${response.status})`);
      }

      await readChatStream(response, (event) => {
        if (event.type === 'status') {
          setStatus(event.message);
        } else if (event.type === 'reply') {
          setStatus('');
          addAssistantMessage(event.content);
        } else if (event.type === 'error') {
          receivedError = true;
          setStatus('');
          addAssistantMessage(`Sorry, something went wrong: ${event.message}`);
        } else if (event.type === 'done') {
          finished = true;
          setStatus('');
          setLoading(false);
        }
      });

      // The server closes the stream right after `done`, so reaching here
      // without one (and without an error event or an abort) means the
      // connection ended mid-run. Say so instead of ending silently.
      if (!finished && !receivedError) {
        addAssistantMessage(
          'The connection ended before the assistant finished. Anything it already did may be partially applied - reply "continue" to let it carry on.'
        );
      }
    } catch (error) {
      if (isAborted(error, controller.signal)) {
        // The user stopped the request: close it out with a note rather than
        // an error, and leave their message in place.
        if (!finished) {
          addAssistantMessage('(stopped)');
        }
      } else {
        addAssistantMessage(
          `Sorry, something went wrong: ${error?.message || 'Something went wrong'}`
        );
      }
    } finally {
      abortRef.current = null;
      setStatus('');
      setLoading(false);
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const handleSend = async () => {
    const text = input.trim();
    const attachments = pendingAttachments;
    if (busy || (!text && attachments.length === 0)) return;

    setInput('');
    setPendingAttachments([]);
    await sendMessages([...messages, { role: 'user', content: text, attachments }]);
  };

  const handleAttachment = async (event) => {
    const files = Array.from(event.target.files || []);
    // Cleared so that picking the same file again still fires a change event.
    event.target.value = '';
    if (files.length === 0 || busy) return;

    const tooLarge = files.find((file) => file.size > MAX_ATTACHMENT_BYTES);
    if (tooLarge) {
      addAssistantMessage(
        `Sorry, ${tooLarge.name} is too large to attach. Files must be under ${Math.floor(
          MAX_ATTACHMENT_BYTES / (1024 * 1024)
        )}MB.`
      );
      return;
    }

    const unsupported = files.find((file) => !isSupportedFile(file));
    if (unsupported) {
      addAssistantMessage(
        `Sorry, ${unsupported.name} cannot be attached. Supported types are ${SUPPORTED_EXTENSIONS.join(
          ', '
        )}.`
      );
      return;
    }

    setAttaching(true);

    try {
      const uploaded = await Promise.all(files.map((file) => uploadAttachment(file)));

      // The files become chips the user can still remove; nothing is sent to
      // the model until they hit send.
      setPendingAttachments((current) => [...current, ...uploaded]);
    } catch (error) {
      const message = error?.response?.data || error?.message || 'Something went wrong';
      addAssistantMessage(`Sorry, that file could not be attached: ${message}`);
    } finally {
      setAttaching(false);
    }
  };

  const removePendingAttachment = (id) =>
    setPendingAttachments((current) => current.filter((attachment) => attachment.id !== id));

  // The icon is the only way into the sidebar, so a workspace without an
  // assistant has nothing to render here - and the server would refuse the chat
  // behind it in any case.
  if (!aiEnabled) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label="AI assistant"
        onClick={() => {
          if (open) {
            handleClose();
          } else {
            setOpen(true);
          }
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: open ? `calc(${drawerWidth}px + 24px)` : 24,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) =>
            theme.transitions.create('right', {
              duration: theme.transitions.duration.enteringScreen
            })
        }}>
        <ChatIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6">AI Assistant</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={startNewChat}
                aria-label="start a new chat"
                title="Start a new chat"
                disabled={loading || restoring}>
                <AddCommentOutlined />
              </IconButton>
              <IconButton
                onClick={handleOpenHistory}
                aria-label="chat history"
                title="Chat history"
                disabled={loading || restoring}>
                <HistoryOutlined />
              </IconButton>
              <IconButton onClick={handleClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            {messages.length === 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
                Ask me anything about Bowlscout.
              </Typography>
            )}
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1.5
                }}>
                <Box
                  sx={{
                    maxWidth: '80%',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    whiteSpace: 'pre-wrap',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                  }}>
                  {message.content && <Typography variant="body2">{message.content}</Typography>}
                  {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: message.content ? 0.5 : 0
                      }}>
                      {message.attachments.map((attachment) => (
                        <Chip
                          key={attachment.id}
                          size="small"
                          label={attachment.name}
                          icon={<InsertDriveFileOutlined />}
                          sx={{
                            maxWidth: '100%',
                            '& .MuiChip-icon': {
                              fontSize: 16,
                              ...(message.role === 'user' && { color: 'inherit' })
                            },
                            ...(message.role === 'user' && {
                              bgcolor: 'rgba(255, 255, 255, 0.22)',
                              color: 'inherit'
                            })
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
            {(loading || restoring) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CircularProgress size={20} />
                {status && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {status} ({elapsedSeconds}s)
                  </Typography>
                )}
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            {pendingAttachments.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {pendingAttachments.map((attachment) => (
                  <Chip
                    key={attachment.id}
                    size="small"
                    variant="outlined"
                    label={attachment.name}
                    icon={<InsertDriveFileOutlined />}
                    onDelete={() => removePendingAttachment(attachment.id)}
                    sx={{ maxWidth: '100%', '& .MuiChip-icon': { fontSize: 16 } }}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_ACCEPT}
                multiple
                hidden
                onChange={handleAttachment}
              />
              <IconButton
                color="primary"
                aria-label="attach files"
                title="Attach PDF, spreadsheet, Word or text files"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}>
                {attaching ? <CircularProgress size={20} /> : <AttachFile />}
              </IconButton>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask the AI assistant..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={busy}
              />
              {loading ? (
                <IconButton
                  color="primary"
                  aria-label="stop"
                  title="Stop the response"
                  onClick={handleStop}>
                  <StopIcon />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  aria-label="send"
                  onClick={handleSend}
                  disabled={busy || (!input.trim() && pendingAttachments.length === 0)}>
                  <SendIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Menu
        anchorEl={historyAnchorEl}
        open={Boolean(historyAnchorEl)}
        onClose={() => setHistoryAnchorEl(null)}
        PaperProps={{ sx: { width: 300, maxWidth: '100%' } }}>
        {historyItems.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">No saved chats yet</Typography>
          </MenuItem>
        ) : (
          historyItems.map((conversation) => (
            <MenuItem
              key={conversation.id}
              disabled={loading}
              onClick={() => handleOpenConversation(conversation.id)}
              sx={{ alignItems: 'flex-start', py: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
                <Typography variant="body2" noWrap>
                  {conversation.title || 'Untitled chat'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatRelativeTime(conversation.updatedAt)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="delete chat"
                onClick={(event) => {
                  // The row's own click loads the chat; the delete must not.
                  event.stopPropagation();
                  handleDeleteConversation(conversation.id);
                }}>
                <DeleteOutlineOutlined fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
